import * as React from "react";
import { Mic, Square } from "lucide-react";

import { apiRequest } from "@/lib/queryClient";
import { cn } from "@/lib/utils";

interface DictationResultItem {
  0: { transcript: string };
  isFinal: boolean;
  length: number;
}

interface DictationResultList {
  item(index: number): DictationResultItem;
  length: number;
}

interface DictationEvent {
  results: DictationResultList;
}

interface DictationErrorEvent {
  error: string;
}

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  onresult: ((event: DictationEvent) => void) | null;
  onerror: ((event: DictationErrorEvent) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
}

interface SpeechRecognitionFactory {
  new (): SpeechRecognitionLike;
}

interface DictationWindow extends Window {
  SpeechRecognition?: SpeechRecognitionFactory;
  webkitSpeechRecognition?: SpeechRecognitionFactory;
}

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  enableDictation?: boolean;
  dictationLanguage?: string;
  dictationAutoRedact?: boolean;
}

function setTextareaValue(node: HTMLTextAreaElement, value: string) {
  const descriptor = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, "value");
  descriptor?.set?.call(node, value);
  node.dispatchEvent(new Event("input", { bubbles: true }));
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      enableDictation = true,
      dictationLanguage = "pl-PL",
      dictationAutoRedact = true,
      disabled,
      ...props
    },
    ref
  ) => {
    const innerRef = React.useRef<HTMLTextAreaElement | null>(null);
    const recognitionRef = React.useRef<SpeechRecognitionLike | null>(null);
    const [isListening, setIsListening] = React.useState(false);
    const [isRedacting, setIsRedacting] = React.useState(false);
    const [speechSupported, setSpeechSupported] = React.useState(false);

    React.useImperativeHandle(ref, () => innerRef.current as HTMLTextAreaElement);

    React.useEffect(() => {
      if (typeof window === "undefined") {
        return;
      }

      const speechWindow = window as DictationWindow;
      const recognitionFactory =
        speechWindow.SpeechRecognition || speechWindow.webkitSpeechRecognition;
      setSpeechSupported(Boolean(recognitionFactory));

      return () => {
        recognitionRef.current?.stop();
        recognitionRef.current = null;
      };
    }, []);

    const appendWithSeparator = React.useCallback((currentValue: string, appendedValue: string) => {
      const trimmedAppend = appendedValue.trim();
      if (!trimmedAppend) {
        return currentValue;
      }

      if (!currentValue.trim()) {
        return trimmedAppend;
      }

      return `${currentValue.trimEnd()} ${trimmedAppend}`;
    }, []);

    const redactTranscript = React.useCallback(
      async (transcript: string) => {
        if (!dictationAutoRedact) {
          return transcript;
        }

        try {
          setIsRedacting(true);
          const response = await apiRequest("POST", "/api/ai/redact-dictation", {
            text: transcript,
          });
          if (typeof response?.text !== "string") {
            return transcript;
          }

          const normalized = response.text.trim();
          return normalized || transcript;
        } catch {
          return transcript;
        } finally {
          setIsRedacting(false);
        }
      },
      [dictationAutoRedact]
    );

    const stopListening = React.useCallback(() => {
      recognitionRef.current?.stop();
      recognitionRef.current = null;
      setIsListening(false);
    }, []);

    const startListening = React.useCallback(() => {
      if (typeof window === "undefined") {
        return;
      }

      const node = innerRef.current;
      if (!node) {
        return;
      }

      const speechWindow = window as DictationWindow;
      const recognitionFactory =
        speechWindow.SpeechRecognition || speechWindow.webkitSpeechRecognition;
      if (!recognitionFactory) {
        return;
      }

      if (recognitionRef.current) {
        recognitionRef.current.stop();
        recognitionRef.current = null;
      }

      const recognition = new recognitionFactory();
      recognition.lang = dictationLanguage;
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.maxAlternatives = 1;

      recognition.onerror = () => {
        setIsListening(false);
        recognitionRef.current = null;
      };

      recognition.onresult = async (event: DictationEvent) => {
        const firstResult = event.results.item(0);
        const transcript = firstResult?.[0]?.transcript?.trim() || "";
        if (!transcript) {
          return;
        }

        const redacted = await redactTranscript(transcript);
        const nextValue = appendWithSeparator(node.value, redacted);
        setTextareaValue(node, nextValue);
      };

      recognition.onend = () => {
        setIsListening(false);
        recognitionRef.current = null;
      };

      recognitionRef.current = recognition;
      setIsListening(true);
      recognition.start();
    }, [appendWithSeparator, dictationLanguage, redactTranscript]);

    const canUseDictation = enableDictation && speechSupported;
    const isBusy = isListening || isRedacting;

    return (
      <div className="relative w-full">
        <textarea
          className={cn(
            "flex min-h-[100px] w-full rounded-md border border-input bg-background px-5 py-4 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            canUseDictation ? "pr-12" : "",
            className
          )}
          ref={innerRef}
          disabled={disabled || isRedacting}
          {...props}
        />

        {canUseDictation && (
          <button
            type="button"
            onClick={isListening ? stopListening : startListening}
            disabled={Boolean(disabled) || isRedacting}
            title={isListening ? "Zatrzymaj dyktowanie" : "Dyktuj i redaguj AI"}
            aria-label={isListening ? "Zatrzymaj dyktowanie" : "Dyktuj i redaguj AI"}
            className={cn(
              "absolute right-2 bottom-2 inline-flex h-8 w-8 items-center justify-center rounded-md border border-white/20 bg-black/60 text-white transition-colors",
              isListening ? "border-red-400/50 text-red-300" : "hover:bg-black/80",
              isBusy ? "cursor-wait" : ""
            )}
          >
            {isListening ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
          </button>
        )}
      </div>
    );
  }
);
Textarea.displayName = "Textarea";

export { Textarea };
